/**
 * Scene splat budget, set together with the budget mode that keeps the meaning it had on
 * PlayCanvas 2.21.
 *
 * PlayCanvas 2.21: a budget of 0 skipped budgeting. Every streamed (LOD) node took the level its
 * distance gives (lodBaseDistance / lodMultiplier), which is what the editor's Custom LOD "Off"
 * promises. A positive budget was balanced both ways: nodes went coarser while the total was over
 * it, and finer (up to the finest level) while it was under.
 *
 * PlayCanvas 2.23 always balances, treats 0 as "no limit" (Infinity), and adds
 * `GSplatParams#splatBudgetMode`:
 * - TARGET, the engine default, spends the budget. With no limit it puts every node at its finest
 *   level, so 0 would now load every streamed node at full detail.
 * - LIMIT never goes finer than the distance LOD and only goes coarser to fit the budget. With no
 *   limit that is exactly 2.21's distance-only LOD.
 *
 * So 0 (Custom "Off", or a `window.__storysplatPerfOverrides` budget of 0) maps to LIMIT, and a
 * positive budget (every LOD preset, performance mode, the VR cap, the compare split's halves)
 * maps to TARGET, 2.21's fill-the-budget balancing.
 */
import * as pc from 'playcanvas';

export type SplatBudgetMode = typeof pc.GSPLAT_BUDGET_TARGET | typeof pc.GSPLAT_BUDGET_LIMIT;

/** The budget mode that gives `budget` its PlayCanvas 2.21 meaning (see the file comment). */
export function splatBudgetModeFor(budget: number): SplatBudgetMode {
    return budget > 0 ? pc.GSPLAT_BUDGET_TARGET : pc.GSPLAT_BUDGET_LIMIT;
}

/** Sets the scene's splat budget and its matching mode. Every writer of the budget goes through this. */
export function setSceneSplatBudget(gsplat: { splatBudget: number; splatBudgetMode: string }, budget: number): void {
    gsplat.splatBudgetMode = splatBudgetModeFor(budget);
    gsplat.splatBudget = budget;
}
